import { useLoading } from 'contextProviders/LoadingBackdropProvider';
import React, { useEffect, useState } from 'react';
import { CANtrackCase } from '../types/comment';
import { debounce } from 'lodash';
import { useNotificationProvider } from 'hooks';
import { Employee } from 'containers/pages/timesheets/types/employee';
import getCases from 'api/comments/getCases';
import { convertToCANtrackCase } from '../utils/convertToCANtrackCase';
import getPinnedCases from 'api/comments/getPinnedCases';
import { useSelector } from 'react-redux';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import { pinCase } from 'api/comments/pinCase';
import { unpinCase } from 'api/comments/unpinCase';
export interface CaseFilters {
	ConsultantId?: number;
	CustomerId?: number;
	Date?: Date;
	Page: number;
	Take: number;
	SearchKeyword?: string;
}

const useCases = () => {
	const [loadingCases, setLoadingCases] = useState<boolean>(false);
	const [pinnedCases, setPinnedCases] = useState<CANtrackCase[]>([]);
	const [cases, setCases] = useState<CANtrackCase[]>([]);
	const [totalCases, setTotalCases] = useState<number>(0);
	const [appliedFilters, setAppliedFilter] = useState<CaseFilters>({
		Page: 1,
		Take: 15,
	});
	const [isSearchingUsingCaseID, setIsSearchingUsingCaseID] =
		useState<boolean>(false); // Flag to determine if case fetch is done using case ID. This is shown in the UI and all other filters are ignored when this is true
	const { notifyError, notifyWarning, notifySuccess } =
		useNotificationProvider();
	const { userInfo } = useSelector(selectUserInfo); // USE THIS TO GET pinned cases

	// Debounce search term. Delay search by 300 ms after user stops typing
	const debouncedSearchTerm = React.useMemo(
		() =>
			debounce((term: string) => {
				setAppliedFilter(
					(prevFilters) =>
						({
							...prevFilters,
							SearchKeyword: term,
						} as CaseFilters),
				);
			}, 300),
		[],
	);

	// Effect to trigger fetch on search term update
	useEffect(() => {
		fetchCases();
	}, [appliedFilters]);

	useEffect(() => {
		fetchPinnedCases();
	}, []);

	const handleSearchChange = (event: any) => {
		debouncedSearchTerm(event.target.value);
	};

	const fetchPinnedCases = async () => {
		try {
			if (!userInfo.userId) {
				throw new Error('userId not found');
			}
			const results = await getPinnedCases(parseInt(userInfo.userId));
			const convertedData = results.map((rawCase: any) => {
				return convertToCANtrackCase(rawCase);
			});
			setPinnedCases(convertedData);
		} catch (error) {
			console.error('Error fetching pinned cases', error);
			notifyError('Error fetching  pinned cases');
		}
	};

	const fetchCases = async () => {
		setLoadingCases(true);

		try {
			let _appliedFilters: any = appliedFilters;
			const isNumeric = /^\d+$/.test(appliedFilters.SearchKeyword || ''); // Check if search term is a number
			if (isNumeric && appliedFilters.SearchKeyword) {
				// If search term is a number, search by case ID. Ignore all other filters
				setIsSearchingUsingCaseID(true);
				_appliedFilters = {
					Id: parseInt(appliedFilters.SearchKeyword),
					Page: appliedFilters.Page,
					Take: appliedFilters.Take,
				};
			} else {
				setIsSearchingUsingCaseID(false);
			}
			const results = await getCases(_appliedFilters);
			const convertedData = results.rawCases.map((rawCase) => {
				return convertToCANtrackCase(rawCase);
			});
			if (appliedFilters.Page === 1) {
				setCases(convertedData);
			} else {
				setCases((prevCases) => [...prevCases, ...convertedData]);
			}
			setTotalCases(results.totalCases);
		} catch (error) {
			console.error('Error fetching cases', error);
			notifyError('Error fetching cases');
		}

		setLoadingCases(false);
	};

	// TODO: Add logic to implement load more when cases.length < totalCases
	const loadMoreItems = () => {
		setAppliedFilter((prevFilters) => ({
			...prevFilters,
			Page: prevFilters.Page + 1,
		}));
	};

	const updateEmployeeInFilter = (employee: Employee | null) => {
		setAppliedFilter(
			(prevFilters) =>
				({
					...prevFilters,
					ConsultantId: employee?.Id || undefined,
				} as CaseFilters),
		);
	};

	const updateCompanyInFilter = (companyId: number | null) => {
		setAppliedFilter(
			(prevFilters) =>
				({
					...prevFilters,
					CustomerId: companyId || undefined,
				} as CaseFilters),
		);
	};

	const updatePinnedCases = async (pinnedCase: CANtrackCase) => {
		try {
			notifyWarning('Updating pinned cases');

			if (pinnedCases.some((c) => c.id === pinnedCase.id)) {
				// Case is already pinned, unpin it
				await unpinCase(parseInt(userInfo.userId), pinnedCase.id);
				setPinnedCases((prevPinnedCases) =>
					prevPinnedCases.filter((c) => c.id !== pinnedCase.id),
				);
			} else {
				// Case is not pinned, pin it
				await pinCase(parseInt(userInfo.userId), pinnedCase.id);
				setPinnedCases((prevPinnedCases) => [
					...prevPinnedCases,
					pinnedCase,
				]);
			}

			notifySuccess('Pinned cases updated');
		} catch (error) {
			console.error('Error updating pinned cases', error);
			notifyError('Error updating pinned cases');
		}
	};

	return {
		loadingCases,
		cases,
		totalCases,
		isSearchingUsingCaseID,
		appliedFilters,
		pinnedCases,
		handleSearchChange,
		setAppliedFilter,
		updateEmployeeInFilter,
		updateCompanyInFilter,
		updatePinnedCases,
	};
};

export default useCases;
